/**
 * Service utilitaire pour le Cerveau Classeur
 * Récupère les données Excel réelles et appelle le backend
 */

/* global Excel */

import { getSettings } from '../store/settings.store';

/**
 * Extrait les formules non vides d'un tableau 2D de formules Office.js
 * et les retourne comme tableau plat de strings
 */
function extractFormulas(formulasGrid: string[][]): string[] {
  const result: string[] = [];
  for (const row of formulasGrid) {
    for (const cell of row) {
      if (typeof cell === 'string' && cell.startsWith('=')) {
        result.push(cell);
      }
    }
  }
  return result;
}

/**
 * Vérifier les permissions d'accès à Excel (lecture seule - ne modifie aucune cellule)
 */
async function checkExcelPermissions(): Promise<boolean> {
  if (typeof Excel === 'undefined') {
    throw new Error(
      "Le contexte Excel n'est pas disponible. Assurez-vous que le complément est correctement initialisé."
    );
  }

  return await Excel.run(async (context) => {
    try {
      const sheets = context.workbook.worksheets;
      sheets.load('items');
      await context.sync();

      return sheets.items.length > 0;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      if (message.includes('ReadOnly')) {
        throw new Error('Le classeur est en lecture seule. Impossible de modifier.');
      }
      if (message.includes('Permission')) {
        throw new Error('Permission refusée. Vérifiez les droits d\'accès.');
      }
      return false;
    }
  });
}

/**
 * Récupère les données du classeur via Office.js
 * Retourne les valeurs (data) et les formules aplaties (formulas: string[])
 */
export async function getWorkbookData() {
  if (typeof Excel === 'undefined') {
    throw new Error(
      "Le contexte Excel n'est pas disponible. Assurez-vous que le complément est correctement initialisé."
    );
  }

  return await Excel.run(async (ctx) => {
    const workbook = ctx.workbook;
    const sheets = workbook.worksheets;

    sheets.load('items');
    await ctx.sync();

    for (const sheet of sheets.items) {
      sheet.load(['name', 'visibility']);
    }
    await ctx.sync();

    const sheetDataList: Array<{
      name: string;
      visibility: string;
      data: unknown[][];
      formulas: string[];
      tables: Array<{ name: string; address: string; columns: string[] }>;
      charts: Array<{ name: string; type: string }>;
    }> = [];

    for (const sheet of sheets.items) {
      try {
        const usedRange = sheet.getUsedRangeOrNullObject();
        usedRange.load(['rowCount', 'columnCount', 'values', 'formulas', 'address']);

        const tables = sheet.tables;
        tables.load('items');

        const charts = sheet.charts;
        charts.load('items');

        await ctx.sync();

        if (usedRange.isNullObject) {
          sheetDataList.push({
            name: sheet.name,
            visibility:
              sheet.visibility === Excel.SheetVisibility.visible ? 'visible' : 'hidden',
            data: [],
            formulas: [],
            tables: [],
            charts: [],
          });
          continue;
        }

        const tableInfosTmp: Array<{
          table: Excel.Table;
          headerRange: Excel.Range;
          fullRange: Excel.Range;
        }> = [];

        for (const table of tables.items) {
          table.load(['name']);

          const headerRange = table.getHeaderRowRange();
          headerRange.load(['values']);

          const fullRange = table.getRange();
          fullRange.load(['address']);

          tableInfosTmp.push({ table, headerRange, fullRange });
        }

        for (const chart of charts.items) {
          chart.load(['name', 'chartType']);
        }

        await ctx.sync();

        const tableInfos = tableInfosTmp.map(({ table, headerRange, fullRange }) => ({
          name: table.name,
          address: fullRange.address,
          columns: ((headerRange.values as unknown[][] | undefined)?.[0] ?? []).map(String),
        }));

        const chartInfos = charts.items.map((c) => ({
          name: c.name,
          type: String(c.chartType),
        }));

        // Build data from already-loaded usedRange.values
        const expandedData: unknown[][] = Array.isArray(usedRange.values)
          ? (usedRange.values as unknown[][]).map((r) => Array.from(r))
          : [];

        // Expand merged cells so dashboards (KPI in merged areas) don't look "full of blanks".
        // getMergedAreas() is a preview API (ExcelApi 1.13+) and may not be available in all
        // environments (Excel Online, older desktop). Wrap in its own try-catch so a failure
        // here never discards the data we already loaded above.
        try {
          const mergedAreasFactory = (usedRange as any)?.getMergedAreas?.();
          if (mergedAreasFactory && typeof mergedAreasFactory.load === 'function') {
            mergedAreasFactory.load('items');
            await ctx.sync();

            for (const area of mergedAreasFactory.items as any[]) {
              area.load(['address', 'values']);
            }
            await ctx.sync();

            const parseCellRef = (ref: string): { row0: number; col0: number } | null => {
              const cleaned = String(ref ?? '').replace(/\$/g, '').trim().toUpperCase();
              const m = /^([A-Z]+)(\d+)$/.exec(cleaned);
              if (!m) return null;
              const colLetters = m[1];
              const row = parseInt(m[2], 10);
              const colLettersToNumber = (letters: string) => {
                let n = 0;
                for (let i = 0; i < letters.length; i++) {
                  n = n * 26 + (letters.charCodeAt(i) - 64);
                }
                return n;
              };
              const col = colLettersToNumber(colLetters);
              return { row0: row - 1, col0: col - 1 };
            };

            const parseRangeAddress = (addr: string): { start: { row0: number; col0: number } } | null => {
              const cleaned = String(addr ?? '').replace(/^.*!/, '');
              const [startRef] = cleaned.split(':');
              const start = parseCellRef(startRef);
              return start ? { start } : null;
            };

            const usedParsed = parseRangeAddress(String(usedRange.address ?? ''));
            const usedStart = usedParsed?.start;
            if (usedStart) {
              for (const area of mergedAreasFactory.items as any[]) {
                const areaAddr = String(area.address ?? '');
                const areaVals: unknown[][] = Array.isArray(area.values) ? (area.values as unknown[][]) : [];
                if (!areaVals.length || !areaVals[0]?.length) continue;

                const areaParsed = parseRangeAddress(areaAddr);
                if (!areaParsed?.start) continue;

                const r0 = areaParsed.start.row0 - usedStart.row0;
                const c0 = areaParsed.start.col0 - usedStart.col0;
                if (r0 < 0 || c0 < 0) continue;

                const fillValue = areaVals[0]?.[0];
                const height = areaVals.length;
                const width = areaVals[0].length;

                for (let rr = 0; rr < height; rr++) {
                  for (let cc = 0; cc < width; cc++) {
                    const tr = r0 + rr;
                    const tc = c0 + cc;
                    if (expandedData[tr] && typeof expandedData[tr][tc] !== 'undefined') {
                      expandedData[tr][tc] = fillValue;
                    }
                  }
                }
              }
            }
          }
        } catch (mergedErr) {
          console.warn('[Workbook Brain] getMergedAreas non disponible, données brutes utilisées:', mergedErr);
        }

        // Aplatir les formules 2D en tableau plat de strings
        const formulasGrid = (usedRange.formulas as string[][]) ?? [];
        const flatFormulas = extractFormulas(formulasGrid);

        sheetDataList.push({
          name: sheet.name,
          visibility:
            sheet.visibility === Excel.SheetVisibility.visible ? 'visible' : 'hidden',
          data: expandedData,
          formulas: flatFormulas,
          tables: tableInfos,
          charts: chartInfos,
        });
      } catch (sheetErr) {
        console.error(`[Workbook Brain] Erreur lecture feuille "${sheet.name}":`, sheetErr);
        sheetDataList.push({
          name: sheet.name,
          visibility:
            sheet.visibility === Excel.SheetVisibility.visible ? 'visible' : 'hidden',
          data: [],
          formulas: [],
          tables: [],
          charts: [],
        });
      }
    }

    return {
      name: 'Workbook',
      sheets: sheetDataList,
      namedRanges: [],
    };
  });
}

/**
 * Appelle l'API d'analyse complète du backend
 */
export async function callAnalyzeAPI(options: {
  includeScanning?: boolean;
  includeUnderstanding?: boolean;
  includeDependencies?: boolean;
  includeRiskAnalysis?: boolean;
  includePerformanceAnalysis?: boolean;
  includeDocumentation?: boolean;
  includeOptimization?: boolean;
}) {
  try {
    // Vérifier les permissions avant de récupérer les données
    await checkExcelPermissions();

    const workbookData = await getWorkbookData();
    const settings = getSettings();

    const response = await fetch('/api/workbook-brain/analyze', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        workbookData,
        ...options,
        provider: settings.provider,
        model: settings.model,
        apiKey: settings.apiKey,
      }),
    });

    if (!response.ok) {
      const err = await response.text();
      throw new Error(`Erreur API (${response.status}) : ${err}`);
    }

    return response.json();
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error('[Workbook Brain API] Erreur:', message);
    throw error;
  }
}

/**
 * Appelle une route spécifique du backend avec un body donné
 */
export async function callSpecificAPI(route: string, body: object) {
  try {
    const response = await fetch(`/api/workbook-brain/${route}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const err = await response.text();
      throw new Error(`Erreur API (${response.status}) : ${err}`);
    }

    return response.json();
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error('[Workbook Brain API] Erreur:', message);
    throw error;
  }
}

/**
 * Compat helper expected by some pages.
 * Delegates to workbook-brain backend route for action plans.
 */
export async function requestWorkbookBrainAIActionPlan(
  payload: string | Record<string, unknown>
) {
  const normalizedPayload =
    typeof payload === 'string' ? { prompt: payload } : payload;
  return callSpecificAPI('ai-action-plan', normalizedPayload);
}
